import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Global timezone preference. Defaults to the user's browser/OS timezone, but
 * the user can override it from the header dropdown. Persisted to localStorage.
 *
 * Use the `useTz()` hook to access:
 *   tz:        current IANA timezone id (e.g. 'America/Bogota')
 *   setTz(id): change preference (also persists)
 *   tzLabel:   human label for the current tz
 *   format(date, opts): formats a Date/ISO string in current tz with given options
 */

// Limited curated list of timezones the app exposes in the picker
export const TIMEZONE_OPTIONS = [
  { id: "America/Bogota", label: "Colombia (UTC-5)" },
  { id: "America/Sao_Paulo", label: "Argentina / Brasília (UTC-3)" },
  { id: "Europe/Madrid", label: "España (UTC+1/+2)" },
  { id: "Australia/Adelaide", label: "Australia · Adelaide (UTC+9:30)" },
  { id: "Australia/Brisbane", label: "Australia · Brisbane (UTC+10)" },
  { id: "Australia/Melbourne", label: "Australia · Melbourne (UTC+10/+11)" },
  { id: "Australia/Sydney", label: "Australia · Sydney (UTC+10/+11)" },
];

const STORAGE_KEY = "polla_tz";

function detectDefaultTz() {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (detected && TIMEZONE_OPTIONS.some((t) => t.id === detected)) return detected;
    // Map common non-listed zones to a sensible default in the curated list.
    if (!detected) return "America/Bogota";
    // Heuristic: if the OS tz starts with America/, default to Bogota; with Europe/, Madrid;
    // with Australia/, Sydney. Else fall back to Bogota.
    if (detected.startsWith("Australia/")) return "Australia/Sydney";
    if (detected.startsWith("Europe/")) return "Europe/Madrid";
    if (detected === "America/Sao_Paulo" || detected.startsWith("America/Argentina/")) return "America/Sao_Paulo";
    return detected.startsWith("America/") ? "America/Bogota" : "America/Bogota";
  } catch {
    return "America/Bogota";
  }
}

const TzContext = createContext({
  tz: "America/Bogota",
  setTz: () => {},
  tzLabel: "Colombia (UTC-5)",
  format: () => "",
});

export function TzProvider({ children }) {
  const [tz, setTzState] = useState(() => {
    if (typeof window === "undefined") return "America/Bogota";
    return localStorage.getItem(STORAGE_KEY) || detectDefaultTz();
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, tz);
    } catch {
      // Private browsing or storage quota: tz stays in memory only.
    }
  }, [tz]);

  const tzLabel = useMemo(
    () => TIMEZONE_OPTIONS.find((o) => o.id === tz)?.label || tz,
    [tz]
  );

  // Locale-aware formatter; picks a Spanish/Portuguese/English locale based on tz family
  const localeFor = (tzId) => {
    if (tzId.startsWith("Europe/Madrid")) return "es-ES";
    if (tzId.startsWith("America/Sao_Paulo")) return "pt-BR";
    if (tzId.startsWith("Australia/")) return "en-AU";
    return "es-CO";
  };

  const format = useMemo(() => {
    return (input, opts = {}) => {
      if (!input) return "";
      try {
        const d = input instanceof Date ? input : new Date(input);
        if (isNaN(d.getTime())) return "";
        return new Intl.DateTimeFormat(localeFor(tz), { timeZone: tz, ...opts }).format(d);
      } catch {
        return "";
      }
    };
  }, [tz]);

  // Stable context value so consumers only re-render when tz/format/label change.
  const value = useMemo(
    () => ({ tz, setTz: setTzState, tzLabel, format }),
    [tz, tzLabel, format]
  );

  return (
    <TzContext.Provider value={value}>
      {children}
    </TzContext.Provider>
  );
}

export function useTz() {
  return useContext(TzContext);
}

// Convenience helpers for common formats
export function useDateTimeFormat() {
  const { format } = useTz();
  return {
    short: (d) => format(d, { month: "short", day: "2-digit" }),
    shortYear: (d) => format(d, { year: "numeric", month: "short", day: "2-digit" }),
    time: (d) => format(d, { hour: "2-digit", minute: "2-digit" }),
    full: (d) => format(d, { weekday: "short", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }),
  };
}
